class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Listen for cart:refresh event (from mobile-sticky-atc and other integrations)
    document.addEventListener('cart:refresh', () => {
      this.refreshDrawer();
    });

    // Also listen for cart:open event
    document.addEventListener('cart:open', () => {
      this.refreshDrawer();
    });

    document.addEventListener('cart:sections-rendered', () => {
      if (!this.classList.contains('active')) return;
      requestAnimationFrame(() => {
        if (this.classList.contains('active') && !this.contains(document.activeElement)) this.activateFocusTrap();
      });
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const wasActive = this.classList.contains('active');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.dispatchEvent(new CustomEvent('cart:drawer-open'));
      if (wasActive) this.activateFocusTrap();
    });

    if (!wasActive) {
      this.addEventListener('transitionend', () => this.activateFocusTrap(), { once: true });
      setTimeout(() => {
        if (this.classList.contains('active') && !this.contains(document.activeElement)) this.activateFocusTrap();
      }, 450);
    }

    document.body.classList.add('overflow-hidden');
    // Hide chat widget
    this.toggleChatWidget(false);
  }

  close() {
    const wasActive = this.classList.contains('active');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    // Show chat widget
    this.toggleChatWidget(true);
    if (wasActive) document.dispatchEvent(new CustomEvent('cart:drawer-close'));
  }

  activateFocusTrap(preferredElement) {
    const container = this.querySelector('.drawer__inner');
    if (!container || !this.classList.contains('active')) return;
    // Default focus target = the dialog CONTAINER (standard dialog pattern), NOT the ✕ button:
    // programmatic focus on the round close button paints a big :focus-visible ring-circle on
    // every drawer open (iOS Safari applies it to script focus). Keyboard users still reach the
    // ✕ via Tab and get the ring then. preferredElement (e.g. qty control after re-render) wins.
    const focusElement =
      (preferredElement && this.contains(preferredElement) && preferredElement) || container;
    if (focusElement === container && !container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }
    trapFocus(container, focusElement);
  }

  toggleChatWidget(show) {
    const chatLauncher = document.getElementById('ccw-launcher-container');
    if (chatLauncher) {
      chatLauncher.style.display = show ? '' : 'none';
    }
    // LINE friend button
    const lineBtn = document.getElementById('line-friend-button');
    if (lineBtn) {
      lineBtn.style.display = show ? '' : 'none';
    }
    // Hide third-party cart nudge / urgency widgets (e.g., "The Final Nudge" by ecomsend)
    // when the cart drawer is open so they don't clutter the UI.
    const nudgeSelectors = [
      '[class*="ecomsend__Popover__Widget"]',
      '[class*="nudge" i]',
      '[id*="nudge" i]',
      '[class*="final-nudge" i]',
      '[id*="final-nudge" i]',
    ];
    const matched = new Set();
    nudgeSelectors.forEach((sel) => {
      try {
        document.querySelectorAll(sel).forEach((el) => {
          if (!el.closest('cart-drawer')) matched.add(el);
        });
      } catch (e) {}
    });
    // Fallback: find any fixed/absolute elements that literally contain "Final Nudge" text
    document.querySelectorAll('div, aside, section, button, a').forEach((el) => {
      if (el.closest('cart-drawer')) return;
      if (matched.has(el)) return;
      const text = (el.textContent || '').trim();
      if (text && /final\s*nudge/i.test(text) && text.length < 80) {
        const pos = getComputedStyle(el).position;
        if (pos === 'fixed' || pos === 'absolute') matched.add(el);
      }
    });
    matched.forEach((el) => {
      el.style.display = show ? '' : 'none';
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // CHARABANC PATCH 2026-08-04 — 原写法对 .drawer__inner 取两次且都不判空。
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    this.productId = parsedState.id;
    if (parsedState.sections && window.__renderCartSections) {
      window.__renderCartSections(parsedState.sections);
    } else {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  refreshDrawer() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}?sections=cart-drawer,cart-icon-bubble`;

    fetch(url)
      .then((r) => r.json())
      .then((sections) => {
        if (window.__renderCartSections) {
          window.__renderCartSections(sections);
        } else {
          if (sections['cart-drawer']) {
            const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
            const newDrawer = doc.querySelector('#CartDrawer');
            if (newDrawer) {
              document.getElementById('CartDrawer').innerHTML = newDrawer.innerHTML;
              const overlay = this.querySelector('#CartDrawer-Overlay');
              if (overlay) overlay.addEventListener('click', this.close.bind(this));
            }
          }
          if (sections['cart-icon-bubble']) {
            const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            const newBubble = doc.querySelector('.shopify-section');
            const bubble = document.getElementById('cart-icon-bubble');
            if (newBubble && bubble) bubble.innerHTML = newBubble.innerHTML;
          }
        }
        this.open();
      })
      .catch((err) => console.error('Cart drawer refresh failed:', err));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// CHARABANC PATCH 2026-08-04 — CartItems 由 assets/cart.js 定义，是单独下载的一个文件。
// 加载顺序本身是对的（theme.liquid 先渲染 cart.js 再渲染本文件，已核实），但那个请求
// 一旦没到（手机信号差、FB/IG 站内浏览器、拦截插件、爬虫资源预算），浏览器会静默跳过
// 它并照常执行本文件 —— 这里当场抛 ReferenceError 并放弃后面所有代码，结果是抽屉能打开
// 但里面的数量 +/− 和删除叉全部失灵。上线后 10.5 小时内命中 16 次，全部是爬虫，
// 尚无真人踩到；同窗口已有 81 次真人 iPhone 站内浏览器的网络失败，属于拆炸弹。
if (typeof CartItems === 'undefined') {
  console.warn('[cart-drawer] cart.js did not execute; <cart-drawer-items> left undefined');
} else {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}
